// Tour step definitions for ScopeTheMarket
// Each tour is an array of step objects compatible with react-joyride
// 
// Special step types:
// - allowInteraction: true  -> Overlay allows clicks through to the target
// - navigateTo: 'stock_detail' -> This step triggers navigation to continue tour
// - requiresScroll: true -> Element is below the fold, auto-scroll before showing
//
// To edit steps later:
// 1. Each step has a unique data-tour attribute as target
// 2. Steps are shown in array order
// 3. Set allowInteraction: true if user needs to click the element
// 4. Set requiresScroll: true for below-fold elements

export const DASHBOARD_TOUR_STEPS = [
  {
    target: '[data-testid="system-status-indicator"]',
    title: 'System Status',
    content: 'This shows when our AI last scanned the market and when the next scan is scheduled. The system monitors S&P 500 stocks for unusual activity every 15 minutes during market hours.',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-testid="featured-stock-card-enhanced"]',
    title: 'Top Ranked Signal',
    content: 'This highlights the stock with the highest unusual activity score right now. The signal strength (0-100) indicates how abnormal the price/volume behavior is compared to its historical pattern.',
    placement: 'bottom',
  },
  {
    target: '[data-testid="time-filter-button"]',
    title: 'Time Filter',
    content: 'Filter anomalies by time range. Use shorter periods (1-4 hours) for intraday research, or longer periods (3-7 days) to see broader patterns. The system tracks anomalies across multiple timeframes.',
    placement: 'bottom',
  },
  {
    target: '[data-testid="micro-metrics-line"]',
    title: 'Session Metrics',
    content: 'Quick stats showing total anomalies detected and the highest signal strength in the current session. This helps you gauge overall market activity levels.',
    placement: 'bottom',
  },
  {
    target: '[data-testid="tip-banner"]',
    title: 'Pro Tip',
    content: 'Helpful tips appear here to guide your analysis. The top 5 stocks typically show the most significant unusual activity worth investigating.',
    placement: 'top',
  },
  {
    // Special "click-to-continue" step - overlay allows clicks through
    target: '[data-testid="anomaly-table-container"], table',
    title: 'Explore Stock Details',
    content: 'Click any stock row to open its detailed analysis page. The tour will continue there, showing you AI insights, charts, news sentiment, and more.',
    placement: 'top',
    allowInteraction: true,  // Allow clicks through overlay
    navigateTo: 'stock_detail',  // Indicates this step triggers navigation
    styles: {
      spotlight: {
        borderRadius: '8px',
      }
    }
  },
  {
    target: '[data-testid="nav-watchlist-locked"], [data-testid="nav-watchlist"]',
    title: 'Watchlist',
    content: 'Save stocks to your watchlist for quick access. Pro subscribers get alerts when watched stocks show unusual activity.',
    placement: 'bottom',
  },
  {
    target: '[data-testid="nav-alerts-locked"], [data-testid="nav-alerts"]',
    title: 'Price Alerts',
    content: 'Set custom alerts for price movements, percentage changes, or anomaly scores. Get notified when your conditions are met. Available for Pro subscribers.',
    placement: 'bottom',
  },
];

// Index of the "click stock" step in DASHBOARD_TOUR_STEPS
export const CLICK_STOCK_STEP_INDEX = 5;

// Index to start Stock Detail tour from (after navigation from Dashboard)
export const STOCK_DETAIL_START_INDEX = 0;

/**
 * Stock Detail Tour Steps - 11 steps covering all key sections
 * Steps 7-9 are an interactive "AI Article Analysis" modal subflow:
 *   - Step 7: Click the button to open modal (requires user click)
 *   - Step 8: Modal opens, click "Fetch & Analyze" (requires user click)
 *   - Step 9: Results load, click X to close modal (requires user click)
 * 
 * For required-action steps:
 * - requiredAction: 'click' means user must click the target to proceed
 * - requiredTarget: the selector that must be clicked
 * - waitForSelector: element to wait for after action (async)
 * - waitForRemoved: element to wait for removal after action
 */
export const STOCK_DETAIL_TOUR_STEPS = [
  // Step 1: Anomaly Move (top-left)
  {
    target: '[data-tour="sd-anomaly-move"]',
    title: 'Anomaly Move',
    content: "This is the unusual % move that triggered the alert. It's the main 'why you're here' signal.",
    placement: 'bottom',
    disableBeacon: true,
  },
  // Step 2: Delayed Price (top-right)
  {
    target: '[data-tour="sd-delayed-price"]',
    title: 'Delayed Price',
    content: 'Price is delayed by 15 minutes. Use this for research - not exact execution.',
    placement: 'left',
  },
  // Step 3: Daily Close Chart
  {
    target: '[data-tour="sd-daily-chart"]',
    title: 'Daily Close Chart',
    content: 'This shows the broader trend using derived daily closes. Helps you see if the move is a breakout or bounce.',
    placement: 'top',
  },
  // Step 4: AI Confidence Score
  {
    target: '[data-tour="sd-ai-confidence"]',
    title: 'AI Confidence',
    content: 'Confidence estimates clarity behind today\'s move (news alignment + volume + trend). Risk reflects volatility/extension.',
    placement: 'left',
  },
  // Step 5: AI Analysis section (below fold - requires scroll)
  {
    target: '[data-tour="sd-ai-analysis"]',
    title: 'AI Summary',
    content: "Here's the plain-English breakdown: what moved, why it may have moved, and what to watch next.",
    placement: 'top',
    requiresScroll: true,
  },
  // Step 6: Related News section (below fold)
  {
    target: '[data-tour="sd-related-news"]',
    title: 'Related News',
    content: 'These are the most relevant articles for the move. Click into any to validate the catalyst.',
    placement: 'top',
    requiresScroll: true,
  },
  
  // ============ INTERACTIVE MODAL SUBFLOW (Steps 7-9) ============
  // Step 7: AI Article Analysis button - REQUIRES CLICK TO OPEN MODAL
  {
    target: '[data-tour="sd-ai-article-btn"]',
    title: 'AI Article Analysis',
    content: 'Click this button to analyze the most relevant news article with AI.',
    placement: 'left',
    requiresScroll: true,
    allowInteraction: true,
    requiredAction: 'click',
    requiredTarget: '[data-tour="sd-ai-article-btn"]',
    waitForSelector: '[data-tour="aa-modal"]',  // Wait for modal to open
    modalSubflow: 'aiArticle',  // Marks start of modal subflow
  },
  // Step 8: Inside modal - REQUIRES CLICK "Fetch & Analyze"
  {
    target: '[data-tour="aa-fetch-analyze"]',
    title: 'Analyze the Article',
    content: "This interface fetches the original article from the link and extracts the catalyst. Click 'Fetch & Analyze' to start.",
    placement: 'top',
    allowInteraction: true,
    requiredAction: 'click',
    requiredTarget: '[data-tour="aa-fetch-analyze"]',
    waitForSelector: '[data-tour="aa-results"]',  // Wait for results to load
    waitTimeout: 15000,  // 15 seconds for analysis
    isModalStep: true,
  },
  // Step 9: Results shown - REQUIRES CLICK X TO CLOSE MODAL
  // Highlight the X button since that's what user needs to click
  {
    target: '[data-tour="aa-close"]',
    title: 'AI Breakdown',
    content: "Here's the key summary, drivers, and risks extracted from the article. Click X to close and continue the tour.",
    placement: 'left',
    allowInteraction: true,
    requiredAction: 'click',
    requiredTarget: '[data-tour="aa-close"]',
    waitForRemoved: '[data-tour="aa-modal"]',  // Wait for modal to close
    isModalStep: true,
    modalSubflowEnd: true,  // Marks end of modal subflow
  },
  // ============ END MODAL SUBFLOW ============
  
  // Step 10: Institutional Activity (below fold)
  {
    target: '[data-tour="sd-institutional"]',
    title: 'Institutional Activity',
    content: 'Shows whether tracked institutions are accumulating or reducing exposure over recent filings.',
    placement: 'top',
    requiresScroll: true,
  },
  // Step 11: Political Activity (below fold)
  {
    target: '[data-tour="sd-political"]',
    title: 'Political Activity',
    content: 'Tracks political disclosures (buy/sell direction). Useful for spotting unusual smart-money behavior.',
    placement: 'top',
    requiresScroll: true,
  },
];

// Simplified steps for when elements might not exist
export const DASHBOARD_TOUR_STEPS_MINIMAL = [
  {
    target: 'body',
    title: 'Welcome to ScopeTheMarket!',
    content: 'This dashboard shows stocks with unusual activity. Our AI scans the S&P 500 every 15 minutes to find price and volume anomalies worth investigating.',
    placement: 'center',
    disableBeacon: true,
  },
  {
    target: '[data-testid="time-filter-button"]',
    title: 'Time Filter',
    content: 'Filter anomalies by time range - from 1 hour to 7 days. Shorter periods show recent signals, longer periods reveal broader patterns.',
    placement: 'bottom',
  },
  {
    target: 'nav, [data-testid="nav-logo"]',
    title: 'Navigation',
    content: 'Use the navigation to access your Watchlist, Alerts, and Settings. Pro features are marked with a lock icon for free users.',
    placement: 'bottom',
  },
];

// Helper to get steps with fallbacks
export const getDashboardSteps = (hasData = true) => {
  return hasData ? DASHBOARD_TOUR_STEPS : DASHBOARD_TOUR_STEPS_MINIMAL;
};

export const getStockDetailSteps = () => {
  return STOCK_DETAIL_TOUR_STEPS;
};

// Check if current step allows interaction (click-through)
export const isInteractionStep = (steps, stepIndex) => {
  return steps[stepIndex]?.allowInteraction === true;
};

// Check if current step triggers navigation
export const isNavigationStep = (steps, stepIndex) => {
  return steps[stepIndex]?.navigateTo !== undefined;
};

// Check if current step requires user action (click)
export const isRequiredActionStep = (steps, stepIndex) => {
  return steps[stepIndex]?.requiredAction === 'click';
};

// Check if current step is inside a modal subflow
export const isModalStep = (steps, stepIndex) => {
  return steps[stepIndex]?.isModalStep === true;
};

// Check if current step starts a modal subflow
export const isModalSubflowStart = (steps, stepIndex) => {
  return steps[stepIndex]?.modalSubflow !== undefined;
};

// Check if current step ends a modal subflow
export const isModalSubflowEnd = (steps, stepIndex) => {
  return steps[stepIndex]?.modalSubflowEnd === true;
};

// Get the required target selector for a step
export const getRequiredTarget = (steps, stepIndex) => {
  return steps[stepIndex]?.requiredTarget;
};

// Get the selector to wait for after action
export const getWaitForSelector = (steps, stepIndex) => {
  return steps[stepIndex]?.waitForSelector;
};

// Get the selector to wait for removal after action
export const getWaitForRemoved = (steps, stepIndex) => {
  return steps[stepIndex]?.waitForRemoved;
};

// Get the timeout for waiting (default 5000ms)
export const getWaitTimeout = (steps, stepIndex) => {
  return steps[stepIndex]?.waitTimeout || 5000;
};
